import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useHistory } from "react-router-dom"
import { SearchOutlined } from "@ant-design/icons"

import CustomTable from "../../../v2-components/Antd/Table"
import CustomButton from "../../../v2-components/Antd/Button"
import CustomSwitch from "../../../v2-components/Antd/Switch"
import CustomSelect from "../../../v2-components/Antd/Select"
import CustomSpin from "../../../v2-components/Antd/Spin"
import CustomAlert from "../../../v2-components/Antd/Alert"

import { useUserService } from "../hooks/useUserService"
import "../styles/common.css"
import "../styles/tables.css"
import "../styles/button.css"

const UsersTable = () => {
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [pharmacies, setPharmacies] = useState([])
    const [filterPharmacy, setFilterPharmacy] = useState("Todas las farmacias")
    const history = useHistory()
    const { fetchAllUsers, updateUserStatus } = useUserService()

    useEffect(() => {
        let isMounted = true

        const loadUsers = async () => {
            try {
                setLoading(true)
                const data = await fetchAllUsers()

                if (isMounted && data.length > 0) {
                    setUsers(data)
                    const uniquePharmacies = [...new Set(data.map((user) => user.pharmacy))]
                    setPharmacies(uniquePharmacies)
                    setError(null)
                }
            } catch (err) {
                if (isMounted) {
                    setError("Error al cargar los usuarios")
                    console.error(err)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        loadUsers()

        return () => {
            isMounted = false
        }
    }, [fetchAllUsers])

    const handleAddUser = useCallback(() => {
        history.push("/v2/admin-user-management/users/add")
    }, [history])

    const handleToggleEnable = useCallback(
        async (userId, email, enabled, userData) => {
            setUsers((prevUsers) => prevUsers.map((user) => (user.email === email ? { ...user, eneable: enabled } : user)))

            const updateData = {
                id: userData._id,
                roles: userData.roles || ["pharmacy"],
                first_name: userData.first_name,
                last_name: userData.last_name,
                eneable: enabled,
            }

            const success = await updateUserStatus(userId, updateData)

            if (!success) {
                setUsers((prevUsers) => prevUsers.map((user) => (user.email === email ? { ...user, eneable: !enabled } : user)))
            }
        },
        [updateUserStatus],
    )

    const handlePharmacyFilterChange = useCallback((value) => {
        setFilterPharmacy(value)
    }, [])

    const handleViewUser = useCallback(
        (email) => {
            history.push(`/v2/admin-user-management/users/${email}`)
        },
        [history],
    )

    const filteredUsers = useMemo(() => {
        return users.filter((user) => filterPharmacy === "Todas las farmacias" || user.pharmacy === filterPharmacy)
    }, [users, filterPharmacy])

    const columns = useMemo(
        () => [
            {
                title: "Correo electrónico",
                dataIndex: "email",
                key: "email",
                sorter: (a, b) => a.email.localeCompare(b.email),
            },
            {
                title: "Rol",
                dataIndex: "roles",
                key: "roles",
                render: (roles) => (Array.isArray(roles) ? roles.join(", ") : roles),
            },
            {
                title: "Farmacia",
                dataIndex: "pharmacy",
                key: "pharmacy",
            },
            {
                title: "Estado",
                dataIndex: "eneable",
                key: "eneable",
                render: (enabled, record) => (
                    <CustomSwitch
                        checked={enabled}
                        onChange={(checked) => handleToggleEnable(record.id, record.email, checked, record)}
                    />
                ),
            },
            {
                title: "Ver más",
                key: "actions",
                render: (_, record) => <SearchOutlined onClick={() => handleViewUser(record.email)} className="action-icon" />,
            },
        ],
        [handleToggleEnable, handleViewUser],
    )

    if (loading && users.length === 0) {
        return (
            <div className="loading-container">
                <CustomSpin size="large" tip="Cargando usuarios..." />
            </div>
        )
    }

    if (error) {
        return <CustomAlert message={error} type="error" />
    }

    return (
        <div className="page-container">
            <div className="content-box">
                <div className="filter-container">
                    <div className="filter-group">
                        <label className="filter-label">Filtrar por farmacia:</label>
                        <CustomSelect value={filterPharmacy} onChange={handlePharmacyFilterChange} style={{ width: 200 }}>
                            <CustomSelect.Option key="Todas las farmacias" value="Todas las farmacias">
                                Todas las farmacias
                            </CustomSelect.Option>
                            {pharmacies.map((pharmacy) => (
                                <CustomSelect.Option key={pharmacy} value={pharmacy}>
                                    {pharmacy}
                                </CustomSelect.Option>
                            ))}
                        </CustomSelect>
                    </div>
                    <CustomButton className="filled-button" type="primary" onClick={handleAddUser}>
                        Agregar
                    </CustomButton>
                </div>

                <div className="table-container">
                    <CustomTable
                        columns={columns}
                        dataSource={filteredUsers}
                        rowKey="email"
                        loading={loading}
                        pagination={{
                            pageSize: 8,
                        }}
                        className="custom-table"
                    />
                </div>
            </div>
        </div>
    )
}

export default React.memo(UsersTable)

